<template>
  <div class="gs-up" :class="'is-' + kind">
    <input
      ref="input"
      class="gs-up-input"
      type="file"
      :accept="accept"
      :multiple="limit > 1"
      @change="onPick"
    >
    <div v-if="kind === 'image'" class="gs-up-images">
      <div
        v-for="item in files"
        :key="item.uid"
        class="gs-up-thumb"
      >
        <img :src="item.url" :alt="item.name">
        <button type="button" class="gs-up-del" @click="remove(item)">删除</button>
      </div>
      <button
        v-if="files.length < limit"
        type="button"
        class="gs-up-add"
        :disabled="uploading"
        @click="open"
      >
        <span class="gs-up-plus">+</span>
        <span>{{ uploading ? '上传中…' : addText }}</span>
      </button>
    </div>
    <div v-else class="gs-up-files">
      <button type="button" class="gs-up-file-btn" :disabled="uploading || files.length >= limit" @click="open">
        {{ uploading ? '上传中…' : addText }}
      </button>
      <ul v-if="files.length" class="gs-up-list">
        <li v-for="item in files" :key="item.uid">
          <span>{{ item.name }}</span>
          <button type="button" @click="remove(item)">移除</button>
        </li>
      </ul>
    </div>
    <p v-if="tip" class="gs-up-tip">{{ tip }}</p>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import { filePreview, uploadFile } from '@/utils/upload'

export default {
  name: 'GsUpload',
  props: {
    value: {
      type: [Array, String],
      default: () => []
    },
    modelValue: {
      type: [Array, String],
      default: undefined
    },
    kind: {
      type: String,
      default: 'image'
    },
    limit: {
      type: Number,
      default: 1
    },
    accept: {
      type: String,
      default: 'image/png,image/jpeg,image/jpg'
    },
    maxSizeMb: {
      type: Number,
      default: 8
    },
    addText: {
      type: String,
      default: '上传文件'
    },
    tip: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      uploading: false,
      previews: {}
    }
  },
  computed: {
    names() {
      const raw = this.modelValue !== undefined ? this.modelValue : this.value
      if (Array.isArray(raw)) return raw.filter(Boolean)
      if (typeof raw === 'string' && raw.trim()) {
        return raw.split(',').map((item) => item.trim()).filter(Boolean)
      }
      return []
    },
    files() {
      return this.names.map((fileName, index) => ({
        uid: fileName + '-' + index,
        name: this.fileLabel(fileName),
        fileName,
        url: this.previews[fileName] || filePreview(fileName)
      }))
    }
  },
  methods: {
    fileLabel(fileName) {
      const str = String(fileName || '')
      const parts = str.split('/')
      return parts[parts.length - 1] || str
    },
    emitNames(list) {
      this.$emit('input', list)
      this.$emit('update:modelValue', list)
      this.$emit('change', list)
    },
    open() {
      if (this.uploading || this.files.length >= this.limit) return
      this.$refs.input && this.$refs.input.click()
    },
    onPick(e) {
      const picked = Array.from((e.target && e.target.files) || [])
      e.target.value = ''
      if (!picked.length) return
      const room = this.limit - this.names.length
      picked.slice(0, room).forEach((file) => this.send(file))
    },
    send(file) {
      const extOk = this.acceptOk(file)
      if (!extOk) {
        ElMessage.error('文件格式不符合要求')
        return
      }
      if (file.size > this.maxSizeMb * 1024 * 1024) {
        ElMessage.error('文件不能超过 ' + this.maxSizeMb + 'MB')
        return
      }
      this.uploading = true
      uploadFile(file).then((data) => {
        const fileName = data.fileName || data.url
        if (!fileName) {
          ElMessage.error('上传失败')
          return
        }
        if (this.kind === 'image') {
          this.previews = Object.assign({}, this.previews, { [fileName]: URL.createObjectURL(file) })
        }
        this.emitNames(this.names.concat(fileName))
      }).catch((err) => {
        ElMessage.error((err && err.message) || '上传失败')
      }).finally(() => {
        this.uploading = false
      })
    },
    acceptOk(file) {
      if (!this.accept) return true
      const name = (file.name || '').toLowerCase()
      const type = (file.type || '').toLowerCase()
      return this.accept.split(',').some((item) => {
        const rule = item.trim().toLowerCase()
        if (!rule) return false
        if (rule.charAt(0) === '.') return name.slice(-rule.length) === rule
        if (rule.indexOf('/*') !== -1) return type.indexOf(rule.replace('/*', '/')) === 0
        return type === rule || name.indexOf(rule.replace('image/', '.').replace('application/', '.')) !== -1
      })
    },
    remove(item) {
      this.emitNames(this.names.filter((name) => name !== item.fileName))
    }
  }
}
</script>

<style scoped>
.gs-up-input {
  display: none;
}
.gs-up-images {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.gs-up-thumb,
.gs-up-add {
  position: relative;
  width: 106px;
  height: 106px;
  border-radius: 10px;
  overflow: hidden;
  box-sizing: border-box;
}
.gs-up-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.gs-up-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px dashed #C5D8F7;
  background: linear-gradient(180deg, #F7FBFF 0%, #FFFFFF 100%);
  color: #5B6B82;
  cursor: pointer;
  font-size: 12px;
}
.gs-up-add:hover {
  border-color: #0165FF;
  color: #0165FF;
}
.gs-up-plus {
  font-size: 28px;
  line-height: 28px;
  color: #0165FF;
}
.gs-up-del {
  position: absolute;
  right: 6px;
  bottom: 6px;
  height: 24px;
  padding: 0 8px;
  border: 0;
  border-radius: 6px;
  background: rgba(17, 24, 36, 0.72);
  color: #fff;
  cursor: pointer;
  font-size: 12px;
}
.gs-up-file-btn {
  height: 36px;
  padding: 0 14px;
  border: 1px solid #0165FF;
  border-radius: 8px;
  background: #fff;
  color: #0165FF;
  cursor: pointer;
}
.gs-up-list {
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
}
.gs-up-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  margin-bottom: 6px;
  background: #F4F8FF;
  border-radius: 8px;
  font-size: 13px;
  color: #1D2B44;
}
.gs-up-list button {
  border: 0;
  background: transparent;
  color: #0165FF;
  cursor: pointer;
}
.gs-up-tip {
  margin: 8px 0 0;
  color: #8A97AB;
  font-size: 12px;
  line-height: 18px;
}
</style>
